import type { ListResponse, ObjectResponse } from '../../../common/types';
import { annotationToolApiV1, getAnnotationToolBasePath } from '../../../lib';
import { ADMIN_PROJECTS_TAG } from '../constants';
import type {
  Annotator,
  ManageProjectResponse,
  MovableEncounterCountResponse,
  PlannedMove,
  ProjectDetailsResponse,
  ProjectsSummaryResponse,
  ReallocationAllocationDetailResponse,
  ReallocationBatchKey,
  User,
} from '../types/response';
import type { SaveProjectEditsRequest } from '../types/types';

export const adminProjectsApiSlice = annotationToolApiV1
  .enhanceEndpoints({
    addTagTypes: [ADMIN_PROJECTS_TAG],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getProjectsToManage: builder.query<ListResponse<ManageProjectResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/projects`;
        },
        providesTags: [ADMIN_PROJECTS_TAG],
      }),

      getProjectsSummary: builder.query<ObjectResponse<ProjectsSummaryResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return `${tempPath}/manage-projects/projects/summary`;
        },
        providesTags: [ADMIN_PROJECTS_TAG],
      }),

      getProjectDetails: builder.query<
        ObjectResponse<ProjectDetailsResponse>,
        { projectId: string }
      >({
        query: ({ projectId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return `${tempPath}/manage-projects/projects/${projectId}`;
        },
        providesTags: [ADMIN_PROJECTS_TAG],
      }),
      updateProjectAllocation: builder.mutation<
        void,
        {
          projectId: string;
          annotators: Annotator[];
          supervisors: User[];
          defaultAllocationMode: boolean;
        }
      >({
        query: ({ projectId, annotators, supervisors, defaultAllocationMode }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/projects/${projectId}/allocation`,
            method: 'PUT',
            body: {
              annotators,
              supervisors,
              defaultAllocationMode,
            },
          };
        },
        invalidatesTags: [ADMIN_PROJECTS_TAG],
      }),
      updateProjectStatus: builder.mutation<
        ObjectResponse<string>,
        { projectId: string; status: string }
      >({
        query: ({ status, projectId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/manage-projects/projects/${projectId}/update-status`,
            method: 'POST',
            body: status,
          };
        },
        invalidatesTags: [ADMIN_PROJECTS_TAG],
      }),

      getReallocationAllocationDetail: builder.query<
        ObjectResponse<ReallocationAllocationDetailResponse>,
        { projectId: string }
      >({
        query: ({ projectId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return `${tempPath}/manage-projects/projects/${projectId}/allocation-detail`;
        },
        providesTags: [ADMIN_PROJECTS_TAG],
      }),

      getMovableEncounterCount: builder.query<
        ObjectResponse<MovableEncounterCountResponse>,
        {
          projectId: string;
          batch: ReallocationBatchKey;
          fromAnnotatorId: string;
          toAnnotatorId: string;
          /** Other active rows in the same batch, used by the backend to return
           * a context-aware movable cap (accounts for sibling outflows and inflows). */
          context: PlannedMove[];
        }
      >({
        query: ({ projectId, batch, fromAnnotatorId, toAnnotatorId, context }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/projects/${projectId}/movable-count`,
            method: 'POST',
            body: { batch, fromAnnotatorId, toAnnotatorId, context },
          };
        },
        providesTags: [ADMIN_PROJECTS_TAG],
      }),

      saveProjectEdits: builder.mutation<
        ObjectResponse<ProjectDetailsResponse>,
        { projectId: string } & SaveProjectEditsRequest
      >({
        query: ({ projectId, ...body }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/projects/${projectId}/edits`,
            method: 'PUT',
            body,
          };
        },
        invalidatesTags: [ADMIN_PROJECTS_TAG],
      }),
    }),
  });

export const {
  useGetProjectsToManageQuery,
  useGetProjectsSummaryQuery,
  useGetProjectDetailsQuery,
  useUpdateProjectAllocationMutation,
  useUpdateProjectStatusMutation,
  useGetReallocationAllocationDetailQuery,
  useGetMovableEncounterCountQuery,
  useSaveProjectEditsMutation,
} = adminProjectsApiSlice;
